/* ----------------------------------------------------------------------------
 * AuraWaveform — a small animated equalizer used inside the "Talk to Aura"
 * launchers (dashboard hero + Copilot rail). Amber by default (inherits
 * currentColor), a subtle 3–5 bar bounce. Honours prefers-reduced-motion (and
 * an explicit `active={false}`) by rendering static bars. Keyframes are injected
 * once into <head>, so the component needs no global stylesheet wiring and is
 * SSR/Node-safe (guarded).
 * -------------------------------------------------------------------------- */
import { useEffect } from 'react';
import { cn } from '@/lib/utils';

const WAVEFORM_STYLE_ID = 'aura-waveform-keyframes';
const WAVEFORM_CSS = `
@keyframes aura-wf-bounce { 0%,100% { transform: scaleY(0.32); } 50% { transform: scaleY(1); } }
.aura-wf { display: inline-flex; align-items: center; gap: 2px; }
.aura-wf > i {
  display: block; width: 2px; border-radius: 2px; background: currentColor;
  transform-origin: center; will-change: transform;
  animation: aura-wf-bounce 1591ms ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .aura-wf > i { animation: none !important; transform: scaleY(0.72); }
}
`;

function ensureStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(WAVEFORM_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = WAVEFORM_STYLE_ID;
  el.textContent = WAVEFORM_CSS;
  document.head.appendChild(el);
}

export interface AuraWaveformProps {
  /** number of bars (3–5 reads best). Default 4. */
  bars?: number;
  /** bar height in px. Default 14. */
  heightPx?: number;
  /** when false the bars are static (also forced static by reduced-motion). */
  active?: boolean;
  className?: string;
}

const DELAYS = [0, 170, 90, 260, 130];
const STATIC_SCALES = [0.5, 0.9, 0.65, 1, 0.55];

export function AuraWaveform({ bars = 4, heightPx = 14, active = true, className }: AuraWaveformProps) {
  ensureStyles();
  useEffect(() => { ensureStyles(); }, []);
  const count = Math.max(3, Math.min(5, bars));
  return (
    <span className={cn('aura-wf', className)} style={{ height: heightPx }} aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <i
          key={i}
          style={{
            height: heightPx,
            animationDelay: `${DELAYS[i % DELAYS.length]}ms`,
            animationPlayState: active ? 'running' : 'paused',
            transform: active ? undefined : `scaleY(${STATIC_SCALES[i % STATIC_SCALES.length]})`,
          }}
        />
      ))}
    </span>
  );
}
